import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as taskListTemplateActions from '@redux/modules/taskListTemplate'

import defaultFormState, { requiredFields } from '@models/taskListTemplate'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { TaskListTemplateModel, TaskListTemplateRequestOptions } from '@domain-group/realhub-backend-api-types'

export const generateUrls = (taskListTemplate?: Partial<TaskListTemplateModel>) => {
  const { id } = taskListTemplate || {}

  return {
    editTaskListTemplateDetailsUrl: `#/qualityControl/taskListTemplates/${id}/edit/details`,
    editTaskListTemplateUrl: `#/qualityControl/taskListTemplates/${id}/edit/`,
    taskListHiddenTemplatesIndexUrl: '#/qualityControl/taskListTemplates?hidden=true',
    taskListTemplatesIndexUrl: '#/qualityControl/taskListTemplates?hidden=false',
  }
}

type CreateTaskListTemplateParams = {
  dispatch: AppDispatch,
  requestOptions?: TaskListTemplateRequestOptions,
  taskListTemplateParams: Partial<TaskListTemplateModel>,
}

const createTaskListTemplate = (params: CreateTaskListTemplateParams) => {
  const { dispatch, taskListTemplateParams, requestOptions } = params
  const { createTaskListTemplate: createFn } = taskListTemplateActions

  return dispatch(createFn(taskListTemplateParams, requestOptions))
}

type UpdateTaskListTemplateParams = {
  dispatch: AppDispatch,
  requestOptions?: TaskListTemplateRequestOptions,
  taskListTemplate: TaskListTemplateModel,
  taskListTemplateParams: Partial<TaskListTemplateModel>,
}

const updateTaskListTemplate = (params: UpdateTaskListTemplateParams) => {
  const {
    dispatch, taskListTemplate, taskListTemplateParams, requestOptions,
  } = params
  const { updateTaskListTemplate: updateFn } = taskListTemplateActions

  const updatedParams = {
    id: taskListTemplate.id,
    ...taskListTemplateParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTaskListTemplateParams = {
  dispatch: AppDispatch,
  taskListTemplate: DeleteParams<TaskListTemplateModel>,
}

const deleteTaskListTemplate = (params: DeleteTaskListTemplateParams) => {
  const { dispatch, taskListTemplate } = params
  const { deleteTaskListTemplate: deleteFn } = taskListTemplateActions

  return dispatch(deleteFn(taskListTemplate))
}

type DuplicateTaskListTemplateParams = {
  dispatch: AppDispatch,
  taskListTemplate: TaskListTemplateModel,
}

const duplicateTaskListTemplate = (params: DuplicateTaskListTemplateParams) => {
  const { dispatch, taskListTemplate } = params

  const { duplicateTaskListTemplate: duplicateFn } = taskListTemplateActions

  return dispatch(duplicateFn(taskListTemplate))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTaskListTemplateForm(
  taskListTemplate: Partial<TaskListTemplateModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const taskListTemplateForm = useForm(
    defaultFormState,
    { entity: taskListTemplate, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [taskListTemplate.id, taskListTemplate.cache_key],
  )

  return {
    ...taskListTemplateForm,
  }
}

export const useRelations = (taskListTemplate: Partial<TaskListTemplateModel> = {}) => {
  const { owner_id, owner_type } = taskListTemplate

  const { brands } = useSelector(reduxState => reduxState.entities)

  const brand = owner_type === 'Brand' && owner_id && brands[owner_id] ? brands[owner_id] : {}

  return {
    brand,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: TaskListTemplateRequestOptions,
}

function useTaskListTemplate(initEntity: Partial<TaskListTemplateModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: taskListTemplate }: { entity: TaskListTemplateModel } = useLatestEntity(initEntity, 'taskListTemplates')
  const { id } = taskListTemplate

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'taskListTemplates',
    'loadTaskListTemplate',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.taskListTemplates)

  return {
    callbacks: {
      createTaskListTemplate: (
        taskListTemplateParams: Partial<TaskListTemplateModel>,
        entityOptions?: TaskListTemplateRequestOptions,
      ) => (
        createTaskListTemplate({ taskListTemplateParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditTaskListTemplate: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTaskListTemplateModal',
        payload: { taskListTemplate },
      }),
      deleteTaskListTemplate: () => deleteTaskListTemplate({ dispatch, taskListTemplate }),
      duplicateTaskListTemplate: () => duplicateTaskListTemplate({ dispatch, taskListTemplate }),
      updateTaskListTemplate: (
        taskListTemplateParams: Partial<TaskListTemplateModel>,
        entityOptions?: TaskListTemplateRequestOptions,
      ) => (
        updateTaskListTemplate({
          taskListTemplate, taskListTemplateParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    taskListTemplate,
    updating,
    urls: generateUrls(taskListTemplate),
  }
}

export default useTaskListTemplate
